import { StyleProvider } from '@ant-design/cssinjs';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ConfigProvider, theme } from 'antd';
import { lazy, StrictMode, Suspense, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createLogger, LOG_NAMESPACES } from '@/utils/logger';

import { persister, queryClient } from '@/utils/queryClient';

import App from './app/App';

import '@/styles/index.css';

const ReactQueryDevtoolsLazy = lazy(() =>
  import('@tanstack/react-query-devtools').then((module) => ({
    default: module.ReactQueryDevtools,
  })),
);

const { defaultAlgorithm } = theme;
const logger = createLogger(LOG_NAMESPACES.APP);

const themeConfig = {
  token: {
    colorPrimary: '#cca349', // --primary-gold
    colorBgBase: '#ffff', // --background
    colorTextBase: '#171717', // --foreground
    colorBgLayout: '#1c1f22', // --logo-background
    colorBorder: 'rgba(171, 181, 190, 1)', // --radio-border
    colorBorderSecondary: 'rgba(210, 210, 211, 1)', // --input-border
    colorPrimaryActive: '#a3823a', // --gold-darker
    colorBgSecondary: 'rgba(27, 33, 41, 1)', // --secondary-dark
    colorText: '#333333', // --primary-text
    controlHeight: 40,
    borderRadius: 4,
    fontFamily: 'Satoshi, sans-serif',
    fontSize: 16,
    algorithm: true,
  },
  components: {
    Button: {
      defaultColor: '#111827',
      defaultBorderColor: '#E9ECEF',
      colorBgContainerDisabled: '#a6a6a6',
      colorTextDisabled: '#fff',
      borderColorDisabled: '#d9d9d9',
      colorBgBase: '#ffff',
      colorText: '#cca349',
      colorBorder: '#cca349',
      contentFontSize: 14,
      algorithm: true,
    },
    Input: {
      colorBorder: 'black',
      colorPrimaryHover: '#cca349',
      colorPrimary: '#333333',
      algorithm: true,
      controlOutline: 'none',
      controlOutlineWidth: 0,
      controlItemBgActive: '#fafafa',
      activeShadow: '0 0 6px rgba(7, 5, 33, 0.3)',
    },
    InputPassword: {
      colorBorder: '#d9d9d9',
      colorPrimaryHover: '#cca349',
      colorPrimary: '#333333',
      algorithm: true,
      controlOutline: 'none',
      controlOutlineWidth: 0,
      controlItemBgActive: '#fafafa',
      activeShadow: '0 0 6px rgba(7, 5, 33, 0.3)',
    },
    OTP: {
      containerGap: 8,
      colorBorder: '#d9d9d9',
      colorPrimaryHover: '#cca349',
      borderRadius: 6,
      algorithm: true,
      controlOutline: 'none',
      controlOutlineWidth: 0,
      controlItemBgActive: '#fafafa',
      activeShadow: '0 0 6px rgba(7, 5, 33, 0.3)',
    },
  },
};

export const Main = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    const hydrate = async () => {
      if (persister.restoreClient) {
        try {
          await persister.restoreClient();
          setIsHydrated(true);
          logger.info('Loaded persisted data from react query');
        } catch (error) {
          logger.error('Error restoring React Query cache:', error);
          setIsHydrated(true); // Continua para no bloquear la UI en caso de error
        }
      } else {
        logger.warn('persister.restoreClient is undefined.');
        setIsHydrated(true); // Evita bloqueo si `restoreClient` no esta disponible
      }
    };

    hydrate();
  }, []);

  if (!isHydrated) {
    return <div>Loading...</div>;
  } // Evita bloquear la UI mientras React Query se rehidrata

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
      <StyleProvider layer>
        <ConfigProvider
          theme={{
            algorithm: defaultAlgorithm,
            ...themeConfig,
          }}
        >
          <App />
        </ConfigProvider>
      </StyleProvider>
      <Suspense fallback={null}>
        {typeof import.meta.env !== 'undefined' && import.meta.env.MODE === 'development' && (
          <ReactQueryDevtoolsLazy initialIsOpen={false} />
        )}
      </Suspense>
    </PersistQueryClientProvider>
  );
};

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <Main />
  </StrictMode>,
);
